import React, { useState, useEffect } from 'react';
import {
  ArrowRight, Check, Compass, DollarSign, Lightbulb, Target,
  TrendingUp, Users, Zap, Play, ChevronRight,
  ArrowUpRight, Award, BarChart2, ExternalLink, User, CheckCircle2, Globe,
  MessageSquare, Eye, Radar, Layers, PieChart, LineChart,
  Calendar, Tag, BookOpen, Star, Orbit, Brain, Book, Settings, 
  Telescope, Sparkles
} from 'lucide-react';

// Import the standalone components from main site
import HeaderNav from './HeaderNav';
import Contact from './Contact';
import FooterLan from './FooterLan';

// --- Color Palette ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',  
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb', 
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff'
};

const SPACING = {
  xs: 'px-2 py-1',
  sm: 'px-4 py-2',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-16 md:py-20 lg:py-24',
  sectionMedium: 'py-12 md:py-16 lg:py-20',
  sectionCompact: 'py-8 md:py-12 lg:py-16',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-8'
};

// --- Reusable Button Component ---
const Button = ({
  href,
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  icon: IconComponent,
  iconPosition = 'right',
  fullWidth = false,
  className = '',
  type = 'button',
  target,
  rel
}) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2 font-semibold
    transition-all duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    ${fullWidth ? 'w-full' : ''}
    whitespace-nowrap
  `;

  const sizeStyles = {
    xs: 'px-3 py-1.5 text-xs rounded-md',
    sm: 'px-4 py-2 text-sm rounded-md',
    md: 'px-5 py-2.5 text-base rounded-lg',
    lg: 'px-6 py-3 text-lg rounded-lg'
  };

  const variants = {
    primary: `bg-primary hover:bg-primaryDark text-white focus-visible:ring-primary shadow-md hover:shadow-lg active:shadow-sm`,
    secondary: `bg-secondary hover:bg-secondaryDark text-black focus-visible:ring-secondary shadow-md hover:shadow-lg active:shadow-sm`,
    outlinePrimary: `bg-transparent border-2 border-primary text-primary hover:bg-primaryLight focus-visible:ring-primary`,
    outlineGray: `bg-transparent border border-lightGray text-darkGray hover:border-mediumGray hover:text-black focus-visible:ring-mediumGray`,
    text: `bg-transparent text-primary hover:text-primaryDark hover:underline focus-visible:ring-primary`,
    dark: `bg-darkGray hover:bg-darkGray/80 text-white focus-visible:ring-darkGray shadow-md hover:shadow-lg active:shadow-sm`
  };

  const iconElement = IconComponent ? <IconComponent size={size === 'lg' ? 20 : size === 'sm' ? 16 : 18} strokeWidth={2.5} /> : null;

  const content = (
    <>
      {iconPosition === 'left' && iconElement}
      {children}
      {iconPosition === 'right' && iconElement}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={`${baseStyles} ${sizeStyles[size]} ${variants[variant]} ${className}`}
        target={target}
        rel={rel}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseStyles} ${sizeStyles[size]} ${variants[variant]} ${className}`}
    >
      {content}
    </button>
  );
};

// --- Subpage Navigation (breadcrumbs) Component ---
const SubpageNav = () => {
  return (
    <div className="bg-offWhite py-3 border-b border-lightGray">
      <div className={SPACING.container}>
        <div className="flex items-center text-sm">
          <a href="/" className="text-mediumGray hover:text-primary transition-colors">Home</a>
          <ChevronRight size={14} className="mx-2 text-mediumGray" />
          <a href="/#services" className="text-mediumGray hover:text-primary transition-colors">Services</a>
          <ChevronRight size={14} className="mx-2 text-mediumGray" />
          <span className="text-primary font-medium">Future Thinking</span>
        </div>
      </div>
    </div>
  );
};

// --- Hero Section for Future Thinking ---
const FutureThinkingHero = () => {
  return (
    <section className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 md:pb-20 lg:pb-24 overflow-hidden">
      {/* Dynamic background gradient */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-secondaryLight via-white to-primaryLight opacity-70"></div>
      
      {/* Abstract shapes for visual interest */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-secondary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      
      <div className={`${SPACING.container} z-10 relative`}>
        <div className="flex flex-col lg:flex-row lg:gap-16 items-center">
          {/* Text Content Area */}
          <div className="lg:w-7/12 text-center lg:text-left">
            <div className="inline-block rounded-full bg-secondaryLight px-4 py-1 mb-5">
              <h2 className="text-sm font-semibold text-secondaryDark uppercase tracking-wide">
                Future Thinking
              </h2>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-black leading-tight mb-6 md:mb-8">
              Strategic Foresight for <span className="text-secondary relative border-b-4 border-secondary pb-1">
                Competitive
              </span> Advantage
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-darkGray mb-8 md:mb-10 lg:mb-12 max-w-2xl mx-auto lg:mx-0">
              Our Future Thinking methodology helps organizations identify emerging trends, anticipate market shifts, and develop proactive innovation strategies that create sustainable competitive advantages.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button href="#explore-library" variant="secondary" size="lg" icon={ArrowRight} className="w-full sm:w-auto">
                Explore Innovation Library
              </Button>
              <Button href="#preview" variant="outlineGray" size="lg" icon={Eye} className="w-full sm:w-auto">
                Get a Free Preview
              </Button>
            </div>

            {/* Key Features section */}
            <div className="mt-10 md:mt-12 lg:mt-16 pt-6 md:pt-8 border-t border-lightGray/60">
               <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center lg:justify-start">
                 <div className="flex items-center justify-center sm:justify-start gap-3">
                   <div className="w-10 h-10 rounded-full bg-secondaryLight flex items-center justify-center">
                     <TrendingUp className="w-5 h-5 text-secondaryDark" />
                   </div>
                   <span className="font-medium text-sm sm:text-base text-black">Strategic Trend Analysis</span>
                 </div>
                 <div className="flex items-center justify-center sm:justify-start gap-3">
                   <div className="w-10 h-10 rounded-full bg-secondaryLight flex items-center justify-center">
                     <Target className="w-5 h-5 text-secondaryDark" />
                   </div>
                   <span className="font-medium text-sm sm:text-base text-black">Opportunity Identification</span>
                 </div>
                 <div className="flex items-center justify-center sm:justify-start gap-3">
                   <div className="w-10 h-10 rounded-full bg-secondaryLight flex items-center justify-center">
                     <CheckCircle2 className="w-5 h-5 text-secondaryDark" />
                   </div>
                   <span className="font-medium text-sm sm:text-base text-black">Risk Mitigation Insights</span>
                 </div>
               </div>
            </div>
          </div>

          {/* Hero Image Area */}
          <div className="lg:w-5/12 mt-12 sm:mt-16 lg:mt-0 w-full max-w-md mx-auto lg:max-w-none lg:mx-0">
            <div className="relative z-20">
              {/* Main image */}
              <div className="rounded-2xl overflow-hidden shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <img
                  src="/img/futurethinking.png"
                  alt="Strategic foresight and trend analysis visualization"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              
              {/* Floating card element */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-4 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center gap-3">
                  <LineChart size={24} className="text-secondaryDark" />
                  <div>
                    <div className="text-xs text-mediumGray font-medium">Trend Analysis</div>
                    <div className="text-lg font-bold text-black">Future-Proof Strategy</div>
                  </div>
                </div>
              </div>
              
              {/* Second floating element */}
              <div className="absolute -top-4 -right-4 bg-secondary/90 text-black rounded-lg shadow-lg py-2 px-4 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="font-bold">150+ Insights & Trends</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Overview Section ---
const OverviewSection = () => {
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className={SPACING.container}>
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-4">
            Navigate Change with <span className="text-secondary">Strategic Foresight</span>
          </h2>
          <p className="text-lg text-darkGray">
            Our structured Future Thinking methodology equips organizations with the tools and insights to anticipate industry shifts, identify emerging opportunities, and develop innovation strategies that turn market disruption into competitive advantage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          <div className="bg-offWhite rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 rounded-lg bg-secondaryLight flex items-center justify-center mb-5">
              <Telescope className="w-6 h-6 text-secondaryDark" />
            </div>
            <h3 className="text-xl font-bold text-black mb-3">Trend Identification & Analysis</h3>
            <p className="text-darkGray">
              Our systematic scanning methodology identifies emerging signals across industries, technologies, and consumer behaviors long before they become mainstream, enabling you to anticipate and prepare for market evolution.
            </p>
          </div>

          <div className="bg-offWhite rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 rounded-lg bg-secondaryLight flex items-center justify-center mb-5">
              <TrendingUp className="w-6 h-6 text-secondaryDark" />
            </div>
            <h3 className="text-xl font-bold text-black mb-3">Strategic Opportunity Mapping</h3>
            <p className="text-darkGray">
              We translate trends into actionable strategic opportunities specific to your industry and business context, creating a roadmap of innovation possibilities that align with your organizational capabilities and goals.
            </p>
          </div>

          <div className="bg-offWhite rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="w-12 h-12 rounded-lg bg-secondaryLight flex items-center justify-center mb-5">
              <Users className="w-6 h-6 text-secondaryDark" />
            </div>
            <h3 className="text-xl font-bold text-black mb-3">Comprehensive Foresight Framework</h3>
            <ul className="space-y-3 text-darkGray">
              <li className="flex items-start">
                <Check size={18} className="text-secondaryDark mt-1 mr-2 flex-shrink-0" />
                <span>Systematic Trend Monitoring</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="text-secondaryDark mt-1 mr-2 flex-shrink-0" />
                <span>Innovation Ecosystem Mapping</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="text-secondaryDark mt-1 mr-2 flex-shrink-0" />
                <span>Cross-Industry Innovation Transfer</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Feature Spotlight Section ---
const FeatureSpotlight = () => {
  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-secondaryLight/30 to-white">
      <div className={SPACING.container}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block rounded-full bg-secondaryLight px-4 py-1 mb-5">
              <h3 className="text-sm font-semibold text-secondaryDark uppercase tracking-wide">
                Feature Spotlight
              </h3>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-6">
              Proprietary Trend Intelligence Platform
            </h2>
            <p className="text-lg text-darkGray mb-8">
              Our advanced trend scanning methodology combines AI-powered data analysis with expert human curation to identify early signals of change across industries, technologies, and consumer behaviors, helping you anticipate market shifts before competitors.
            </p>
            
            <div className="space-y-4 mb-8">
              {[
                "Continuous monitoring of thousands of sources across 27 industries",
                "Early identification of weak signals and emerging patterns",
                "Proprietary significance scoring and impact analysis framework",
                "Industry-specific filtering and strategic relevance assessment"
              ].map((item, i) => (
                <div key={i} className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-secondaryLight flex-shrink-0 flex items-center justify-center mt-0.5 mr-3">
                    <Check size={14} className="text-secondaryDark" />
                  </div>
                  <p className="text-darkGray">{item}</p>
                </div>
              ))}
            </div>
            
            <Button href="#explore-library" variant="secondary" size="lg" icon={ArrowRight}>
              Explore Our Trend Intelligence
            </Button>
          </div>
          
          <div className="relative">
            <div className="relative z-10 rounded-xl overflow-hidden shadow-xl">
              <img 
                src="https://placehold.co/600x400/fdb52a/ffffff?text=Trend+Intelligence"
                alt="Trend intelligence platform visualization" 
                className="w-full"
              />
            </div>
            
            {/* Decorative elements */}
            <div className="absolute top-8 -left-8 w-40 h-40 bg-secondaryLight/50 rounded-full -z-10"></div>
            <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-secondary/30 rounded-lg -z-10 transform rotate-12"></div>
            
            {/* Floating data cards */}
            <div className="absolute top-6 right-6 bg-white rounded-lg shadow-lg p-4 z-20 max-w-[200px]">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles size={20} className="text-secondaryDark" />
                <h4 className="font-bold text-black">Emerging Trends</h4>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm">AI Integration</span>
                  <div className="w-24 h-3 bg-lightGray rounded-full overflow-hidden">
                    <div className="w-4/5 h-full bg-secondary"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Sustainability</span>
                  <div className="w-24 h-3 bg-lightGray rounded-full overflow-hidden">
                    <div className="w-3/4 h-full bg-secondary"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Remote Work</span>
                  <div className="w-24 h-3 bg-lightGray rounded-full overflow-hidden">
                    <div className="w-3/5 h-full bg-secondary"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- What You Get Section ---
const WhatYouGetSection = () => {
  const offerings = [
    {
      icon: TrendingUp,
      title: "Trend Discovery Dashboard",
      description: "Interactive visualization platform providing real-time trend data and insights customized to your industry and strategic priorities."
    },
    {
      icon: BookOpen,
      title: "Strategic Insight Reports",
      description: "In-depth analysis of high-impact trends and their business implications, delivered as comprehensive quarterly reports."
    },
    {
      icon: PieChart,
      title: "Quantitative Trend Analytics",
      description: "Data-driven assessment of trend momentum, market penetration, and potential business impact to inform strategic decision-making."
    },
    {
      icon: Book,
      title: "Innovation Case Studies",
      description: "Detailed examination of successful innovation strategies across industries, highlighting transferable insights and methodologies."
    },
    {
      icon: Calendar,
      title: "Monthly Trend Briefings",
      description: "Executive-level summaries of emerging trends and strategic implications, delivered as concise monthly updates."
    },
    {
      icon: Compass,
      title: "Strategic Opportunity Mapping",
      description: "Collaborative workshops to translate trend insights into actionable innovation opportunities aligned with your strategic objectives."
    }
  ];

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-white">
      <div className={SPACING.container}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-4">
            Comprehensive <span className="text-secondary">Foresight</span> Resources
          </h2>
          <p className="text-lg text-darkGray">
            Our Future Thinking service delivers a complete suite of strategic foresight tools and resources to help your organization anticipate change and drive innovation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {offerings.map((offering, index) => (
            <div key={index} className="bg-offWhite rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-14 h-14 rounded-lg bg-secondaryLight flex items-center justify-center mb-6">
                {React.createElement(offering.icon, { size: 28, className: 'text-secondaryDark' })}
              </div>
              <h3 className="text-xl font-bold text-black mb-3">{offering.title}</h3>
              <p className="text-darkGray">{offering.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Stats Section (Redesigned without black background) ---
const StatsSection = () => {
  const stats = [
    {
      value: "33%",
      label: "increase in profitability for organizations that systematically implement strategic foresight practices",
      source: "(McKinsey Global Survey)"
    },
    {
      value: "62%",
      label: "of executives confirm their organization's success depends directly on quality of future insights",
      source: "(PwC Innovation Benchmark)"
    },
    {
      value: "200%",
      label: "higher market performance demonstrated by companies with established foresight capabilities",
      source: "(Future Platform Research)"
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-primaryLight to-secondaryLight">
      <div className={SPACING.container}>
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-darkGray">
            The Business <span className="text-primary">Impact</span> of Strategic Foresight
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-8 bg-white rounded-xl border border-lightGray shadow-lg">
              <div className="text-4xl sm:text-5xl font-bold text-primary mb-4">{stat.value}</div>
              <p className="text-darkGray mb-2">{stat.label}</p>
              <p className="text-sm text-mediumGray">{stat.source}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- CTA Section ---
const CTASection = () => {
  return (
    <section id="explore-library" className="py-16 md:py-20 bg-gradient-to-br from-primaryLight/50 via-white to-secondaryLight/50">
      <div className={SPACING.container}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mb-6">
              Access Our Library of <span className="text-secondary">150+</span> Strategic Trends and Insights
            </h2>
            <p className="text-lg text-darkGray mb-8">
              Gain immediate access to our comprehensive collection of trends, case studies, and strategic foresight resources tailored to your industry needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button href="#explore-library" variant="secondary" size="lg" icon={ArrowRight}>
                Explore Innovation Library
              </Button>
              <Button href="#preview" id="preview" variant="outlineGray" size="lg" icon={Eye}>
                Request a Free Preview
              </Button>
            </div>
          </div>
          
          <div className="relative">
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, index) => (
                <div key={index} className="aspect-square bg-white rounded-lg shadow-md flex items-center justify-center p-2 transform hover:scale-105 transition-transform">
                  <div className="text-center">
                    <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-secondaryLight/70 flex items-center justify-center">
                      {React.createElement([Lightbulb, Radar, Brain, Telescope, Star, Orbit, TrendingUp, Users, Settings][index % 9], 
                        { size: 16, className: 'text-secondaryDark' })}
                    </div>
                    <div className="text-xs font-medium text-darkGray">Trend #{index+1}</div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-white/90 pointer-events-none"></div>
            
            {/* Call to action badge */}
            <div className="absolute bottom-4 right-4 bg-secondary text-black font-bold px-4 py-2 rounded-full shadow-lg transform rotate-3 animate-pulse">
              150+ Strategic Insights
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Newsletter Section ---
const NewsletterSection = () => {
  return (
    <section className="py-16 md:py-20 bg-offWhite">
      <div className={SPACING.container}>
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-lightGray">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4">
                Subscribe to Our <span className="text-primary">Foresight</span> Digest
              </h2>
              <p className="text-darkGray mb-6">
                Receive monthly trend insights and strategic foresight analysis directly to your inbox
              </p>
              <p className="text-darkGray mb-8">
                Our expert analysts curate the most significant emerging trends and strategic opportunities across industries, technologies, and consumer behaviors.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <input 
                  type="email" 
                  placeholder="Your work email" 
                  className="w-full px-4 py-3 rounded-lg border border-lightGray focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button variant="secondary" size="md" icon={ArrowRight} className="whitespace-nowrap">
                  Subscribe
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://placehold.co/400x300/fdb52a/ffffff?text=Foresight+Digest"
                  alt="Foresight digest preview" 
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
              
              {/* Decorative elements */}
              <div className="absolute top-6 -left-6 w-20 h-20 bg-secondaryLight rounded-lg -z-10 transform -rotate-6"></div>
              <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-primaryLight rounded-full -z-10"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Main Component for Future Thinking Subpage ---
const FutureThinkingPage = () => {
  return (
    <div className="font-sans antialiased text-darkGray bg-white overflow-x-hidden">
      <HeaderNav />
      <SubpageNav />
      
      <main className="space-y-8 md:space-y-0">
        <FutureThinkingHero />
        <OverviewSection />
        <FeatureSpotlight />
        <WhatYouGetSection />
        <StatsSection />
        <CTASection />
        <NewsletterSection />
        <Contact />
      </main>
      
      <FooterLan />
    </div>
  );
};

export default FutureThinkingPage;